/**
 * A dialog over the page: it takes the keyboard (Tab stays inside, Esc closes), says what it is to a
 * screen reader, and hands focus back to whatever opened it.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Close } from './icons';

export function Dialog({ title, onClose, children, footer, size = 'normal', closable = true }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; size?: 'normal' | 'wide'; closable?: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    const el = box.current!;
    const focusables = () => [...el.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])')];
    (el.querySelector<HTMLElement>('[autofocus], input, select') ?? focusables()[0] ?? el).focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closable) {
        e.stopPropagation();
        close.current();
      } else if (e.key === 'Tab') {
        const list = focusables();
        if (!list.length) return;
        const first = list[0]!;
        const last = list[list.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    el.addEventListener('keydown', onKey);
    return () => {
      el.removeEventListener('keydown', onKey);
      before?.focus?.({ preventScroll: true });
    };
  }, [closable]);
  return createPortal(
    <div className="dialog-backdrop" onPointerDown={(e) => e.target === e.currentTarget && closable && onClose()}>
      <div ref={box} className={`dialog ${size}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <header>
          <h2 id={titleId}>{title}</h2>
          {closable && (
            <button type="button" className="icon" aria-label="Close" onClick={onClose}>
              <Close />
            </button>
          )}
        </header>
        <div className="dialog-body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

/** A small pop-up list of actions, opened by a button. */
export function Menu({ label, items, icon }: { label: string; icon: ReactNode; items: ({ label: string; run: () => void; danger?: boolean } | null)[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    addEventListener('pointerdown', away, true);
    addEventListener('keydown', esc);
    ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    return () => {
      removeEventListener('pointerdown', away, true);
      removeEventListener('keydown', esc);
    };
  }, [open, setOpen]);
  return (
    <div className="popmenu" ref={ref}>
      <button type="button" className="icon" aria-label={label} title={label} aria-haspopup="menu" aria-expanded={open} onClick={(e) => (e.stopPropagation(), e.preventDefault(), setOpen(!open))}>
        {icon}
      </button>
      {open && (
        <div className="popmenu-list" role="menu">
          {items.filter(Boolean).map((item) => (
            <button
              key={item!.label}
              type="button"
              role="menuitem"
              className={item!.danger ? 'danger-text' : undefined}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setOpen(false);
                item!.run();
              }}
            >
              {item!.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
